import * as React from 'react'
import { Button, InfoLabel, mergeClasses, Tooltip } from '@fluentui/react-components'
import { ChevronLeftRegular, ChevronRightRegular } from '@fluentui/react-icons'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { formatTimeInMinutes } from '../../../../helpers/numberHelper'
import useCurrencies from '../../../../hooks/useCurrencies'
import { useSettings } from '../../../../hooks/useSettings'
import CalendarColorsSwitch from './CalendarColorsSwitch'
import CalendarTotals from './CalendarTotals'
import { formatAmounts } from './CalendarTurnover'
import { useTimeCalendarStyles } from './timeCalendarStyles'
import { DailyTotalDto, sumMonth } from './timeCalendarHelper'

type Props = {
    visibleMonth: DateTime
    // The days of the visible month; everything the info label shows is derived from them.
    monthDays: DailyTotalDto[]
    normInMinutes?: number
    stacked: boolean
    // The office can leave the colours to the user (HAM-4315); only then is there a switch to render.
    showColorsSwitch: boolean
    colorsVisible: boolean
    onToggleColors: (visible: boolean) => void
    onJumpMonth: (delta: number) => void
}

/**
 * Month navigation on the left, and on the right the turnover and hours of the visible month. That summary is
 * an info label: hovering it breaks the hours down by billability and the turnover into invoiced, still to be
 * invoiced and the expected total.
 */
export default function CalendarHeader(props: Props) {
    const styles = useTimeCalendarStyles()
    const { t: translate, i18n } = useTranslation()
    const { getCurrency } = useCurrencies()
    const { settings } = useSettings()

    const monthLabel = props.visibleMonth.setLocale(i18n.language).toFormat('LLLL yyyy')
    const capitalizedMonth = monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)
    const totals = sumMonth(props.monthDays)

    // Nothing written means no currency to report either; fall back to the office default so the summary keeps
    // the same shape.
    const monthTurnover = formatAmounts(
        totals.expected.length ? totals.expected : [{ currency: settings?.fileScaffold?.currency || 'EUR', amount: 0 }],
        getCurrency,
    )
    const summary = `${monthTurnover} / ${formatTimeInMinutes(totals.minutes)}`

    return (
        <div className={mergeClasses(styles.header, props.stacked && styles.headerStacked)}>
            <div className={styles.headerGroup}>
                <Tooltip withArrow relationship={'label'} content={translate('time.calendar.navigation.previousmonth')}>
                    <Button appearance={'subtle'} size={'small'} icon={<ChevronLeftRegular />} onClick={() => props.onJumpMonth(-1)} />
                </Tooltip>
                <span className={styles.monthLabel}>{monthLabel}</span>
                <Tooltip withArrow relationship={'label'} content={translate('time.calendar.navigation.nextmonth')}>
                    <Button appearance={'subtle'} size={'small'} icon={<ChevronRightRegular />} onClick={() => props.onJumpMonth(1)} />
                </Tooltip>
            </div>

            <div className={styles.headerGroup}>
                <InfoLabel
                    className={styles.norm}
                    info={<CalendarTotals title={capitalizedMonth} days={props.monthDays} normInMinutes={props.normInMinutes} />}
                >
                    {summary}
                </InfoLabel>

                {props.showColorsSwitch && <CalendarColorsSwitch checked={props.colorsVisible} onChange={props.onToggleColors} />}
            </div>
        </div>
    )
}
